import React from "react";
import { BASE_URL } from "../../utils/serverContextPath";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { getLabel } from "../../utils/languageUtils";
import { hasRole } from "../../pages/userapp/ServiceUserApp";
import ConfirmationModal from "../../components/home/ConfirmationModal";

export default function ConsultationRequestCard({
    isAdmin,
    request,
    mode = "SENDER",
    currentUser,
    onView,
    onEdit,
    onDelete
}) {

    const { t } = useTranslation();
    const { language } = useLanguage();
    

    // ============================================================
    // DONNÉES
    // ============================================================

    const sender = request.sender || {};
    const receiver = request.receiver || {};
    const person =
        mode === "RECEIVER"
            ? sender
            : receiver;
    // ============================================================
    // PHOTO
    // ============================================================

    const defaultAvatar =
        "https://cdn-icons-png.flaticon.com/512/847/847969.png";

    const photo =
        person.photoUrl
            ? `${BASE_URL}userapp/downloadprofile/${person.id}`
            : defaultAvatar;
    // ============================================================
    // DROITS DE MODIFICATION
    // ============================================================
    const canEdit =
        mode === "SENDER" &&
        currentUser &&
        sender.id === currentUser.id;
    // ============================================================
    // TYPE DE CONSULTATION
    // ============================================================

    const consultationType = () => {
        switch (request.consultationType) {
            case "L":
                return t("consultant.types.livestock");
            case "C":
                return t("consultant.types.agriculture");
            default:
                return "-";
        }
    };


    // ============================================================
    // FORMAT DATE
    // ============================================================

    const formatDate = (date) => {
        if (!date) {
            return t("consultationRequest.notSpecified");
        }
        return new Date(date).toLocaleDateString(
            language === "ar"
                ? "ar-DZ"
                : language === "ag"
                    ? "en-US"
                    : "fr-FR",
            {
                day: "2-digit",
                month: "long",
                year: "numeric"
            }
        );
    };


    // ============================================================
    // SUPPRESSION
    // ============================================================

    const handleDelete = () => {
        if (!isAdmin) {
            return;
        }
        if (!onDelete) {
            console.error(
                "onDelete n'est pas défini dans ConsultationRequestCard"
            );
            return;
        }

            if (typeof onDelete === "function") {
            onDelete(request.id);

        } else {

            console.error(
                "consultationRequest : onDelete n'est pas défini."
            );
        }
    };

    // ============================================================
    // AFFICHAGE
    // ============================================================

    return (
        <div className="card consultant-card h-100">
            <hr className="my-4" />
            {/* ==================================================
                BLOC DEMANDE
               ================================================== */}
            <div className="consultation-request-box">
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h2
                            className="fw-bold text-success mb-1"
                            style={{
                                fontSize: "2rem",
                                letterSpacing: "0.5px"
                            }}
                        >
                            <i className="bi bi-chat-left-text-fill me-3"></i>
                            {t("consultationRequest.title")}
                            <span
                                className="ms-2 text-success"
                                style={{
                                    fontSize: "2.2rem",
                                    fontWeight: "800"
                                }}
                            >
                                N° {request.id}
                            </span>
                        </h2>
                        <small className="text-muted">
                            {t("button.create", {
                                date: formatDate(
                                    request.dateConsultReq
                                )
                            })}
                        </small>
                    </div>
                </div>
                <h4 className="fw-bold mb-3">
                    {request.consultationObject}
                </h4>
                <p
                    className="text-muted"
                    style={{
                        lineHeight: "1.8",
                        textAlign: "justify"
                    }}
                >

                    {
                        request.consultationBody
                            ?
                            request.consultationBody.length > 300
                                ?
                                request.consultationBody.substring(
                                    0,
                                    300
                                ) + "..."
                                :
                                request.consultationBody
                            :
                            t(
                                "consultationRequest.noDescription"
                            )
                    }
                </p>
            </div>


            {/* ==================================================
                PIED INFORMATIONS
               ================================================== */}
            <div className="row mt-4">
                <div className="col-md-6">
                    <div className="consultant-info">
                        <i className="bi bi-person-circle me-2"></i>
                        <strong>
                            {
                                mode === "RECEIVER"
                                    ? t(
                                        "consultationRequest.requester"
                                    )
                                    : t(
                                        "consultationRequest.consultant"
                                    )
                            }
                        </strong>
                        {" "}
                        {
                            mode === "RECEIVER"
                                ? sender.fullName
                                : receiver.fullName
                        }

                    </div>
                </div>
                <div className="col-md-6 text-md-end">
                    {
                        request.etatRequest &&
                        <span
                            className="badge bg-warning text-dark fs-6"
                        >
                            {
                                request.etatRequest.libelle ||
                                request.etatRequest.designation ||
                                request.etatRequest.code
                            }
                        </span>
                    }
                </div>
            </div>
            {/* ==================================================
                HEADER VERT
               ================================================== */}
            <div className="consultant-card-header">
            </div>
            {/* ==================================================
                BODY
               ================================================== */}
            <div className="card-body">
                {/* ==================================================
                    PHOTO + INFORMATIONS
                   ================================================== */}
                <div className="row">
                    {/* ==================================================
                        PHOTO
                       ================================================== */}
                    <div className="col-lg-3 text-center">
                        <img
                            src={photo}
                            alt={person.fullName}
                            className="consultant-photo shadow"
                            onError={(e) => {
                                e.target.src =
                                    defaultAvatar;

                            }}
                        />


                        <div className="mt-3">
                            <span className="badge badge-type">
                                {consultationType()}

                            </span>
                        </div>
                    </div>
                    {/* ==================================================
                        INFORMATIONS
                       ================================================== */}
                    <div className="col-lg-9">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h2 className="consultant-name">
                                    {person.fullName}
                                </h2>
                                <div className="consultant-info">
                                    <i className="bi bi-geo-alt-fill"></i>

                                    {" "}

                                    {
                                        getLabel(
                                            person.wilaya,
                                            "wilaya",
                                            language
                                        )
                                    }

                                </div>
                                <div className="consultant-info">
                                    <i className="bi bi-envelope-fill"></i>
                                    {" "}
                                    {person.email || "-"}
                                </div>
                                <div className="consultant-info">
                                    <i className="bi bi-telephone-fill"></i>
                                    {" "}
                                    {person.phoneNumber || "-"}
                                </div>

                                <div className="consultant-info">
                                    <i className="bi bi-globe"></i>
                                    {" "}
                                    {person.webSide || "-"}
                                </div>
                            </div>
                            <div className="text-end">
                                <div className="consultant-experience">
                                    <i className="bi bi-award-fill me-2"></i>
                                    <strong>
                                        {t(
                                            "consultant.card.yearsExperience",
                                            {
                                                count:
                                                    person.nbAnneesExperience ||
                                                    0
                                            }
                                        )}
                                    </strong>
                                </div>
                            </div>
                        </div>
                        <hr />
                        {/* ==================================================
                            ACTIVITÉS PROFESSIONNELLES
                           ================================================== */}
                        {
                            person.activityProfs?.length > 0 &&
                            <div className="consultant-section">
                                <div className="consultant-section-title">
                                    <i className="bi bi-briefcase-fill me-2 text-success"></i>
                                    {t("menu.activityProf")}
                                </div>
                                <div className="badges-container">
                                    {
                                        person.activityProfs.map(
                                            (a) => (
                                                <span
                                                    key={a.id}
                                                    className="badge badge-activity"
                                                >
                                                    {
                                                        getLabel(
                                                            a,
                                                            "activityProf",
                                                            language
                                                        )
                                                    }
                                                </span>
                                            )
                                        )
                                    }
                                </div>
                            </div>
                        }
                        {/* ==================================================
                            SPÉCIALITÉS
                           ================================================== */}
                        {
                            person.specialites?.length > 0 &&
                            <div className="consultant-section">
                                <div className="consultant-section-title">
                                    <i className="bi bi-patch-check-fill me-2 text-success"></i>
                                    {t("menu.speciality")}

                                </div>

                                <div className="badges-container">
                                    {
                                        person.specialites.map(
                                            (s) => (
                                                <span
                                                    key={s.id}
                                                    className="badge badge-specialite"
                                                >
                                                    {
                                                        getLabel(
                                                            s,
                                                            "specialite",
                                                            language
                                                        )
                                                    }
                                                </span>
                                            )
                                        )
                                    }
                                </div>
                            </div>
                        }

                    </div>
                </div>
            </div>

            {/* ==================================================
                FOOTER
               ================================================== */}
            <div className="card-footer bg-white consultant-footer">
                <div className="row align-items-center">
                    {/* ==================================================
                        BOUTON DÉTAIL
                       ================================================== */}
                    <div className="col-lg-4 mb-2 mb-lg-0">

                        <button
                            type="button"
                            className="btn btn-outline-success consultant-btn btn-profile"
                            onClick={() => onView(request)}
                        >

                            <i className="bi bi-eye-fill me-2"></i>

                            {t("button.detail")}
                        </button>
                    </div>

                    {/* ==================================================
                        BOUTON SUPPRIMER - ADMIN UNIQUEMENT
                       ================================================== */}

                    {
                        isAdmin &&
                        <div className="col-lg-4 mb-2 mb-lg-0">
                            <button
                                type="button"
                                className="btn btn-outline-danger consultant-btn"
                                onClick={handleDelete}
                            >
                                <i className="bi bi-trash-fill me-2"></i>
                                {t("button.delete")}
                            </button>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}